import * as React from 'react'
import { Icon } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/misc.tsx'

interface Beneficio {
	id: string
	icon: string
	title: string
	description: string
}

export interface SegurosBeneficiosSectionProps {
	/**
	 * Section title
	 */
	title?: string
	/**
	 * List of benefits to display
	 */
	beneficios?: Beneficio[]
	/**
	 * Additional CSS classes
	 */
	className?: string
}

const defaultBeneficios: Beneficio[] = [
	{
		id: 'sostenibilidad',
		icon: 'eco-power',
		title: 'Sostenibilidad integrada',
		description:
			'Cada póliza contribuye a proyectos de conservación y sostenibilidad.',
	},
	{
		id: 'cotizacion',
		icon: 'agreement-02',
		title: 'Cotización Simple y rápida',
		description: 'Nos aseguramos de que el proceso sea fácil y transparente.',
	},
	{
		id: 'comunidad',
		icon: 'user-multiple',
		title: 'Compromiso con la comunidad',
		description: 'Únete a una comunidad comprometida con el cambio positivo.',
	},
]

const SegurosBeneficiosSection = React.forwardRef<
	HTMLElement,
	SegurosBeneficiosSectionProps
>(
	(
		{
			title = 'Beneficios Clave',
			beneficios = defaultBeneficios,
			className,
			...props
		},
		ref,
	) => {
		return (
			<section
				ref={ref}
				className={cn(
					'relative overflow-hidden bg-gradient-to-br from-blue-100/30 via-white to-green-50/30 py-12 md:py-16',
					className,
				)}
				{...props}
			>
				{/* Background decorative elements */}
				<div className="absolute inset-0 overflow-hidden">
					{/* Decorative lines pattern */}
					<div className="absolute -top-32 -right-32 h-96 w-96 rotate-45 bg-gradient-to-br from-blue-200/20 to-transparent"></div>
					<div className="absolute -bottom-32 -left-32 h-96 w-96 rotate-45 bg-gradient-to-br from-green-200/20 to-transparent"></div>
					{/* Additional decorative elements */}
					<div className="absolute top-1/4 right-1/4 h-2 w-2 rounded-full bg-blue-300/40"></div>
					<div className="absolute top-1/3 right-1/3 h-1 w-1 rounded-full bg-green-300/40"></div>
					<div className="absolute bottom-1/4 left-1/4 h-1.5 w-1.5 rounded-full bg-blue-300/40"></div>
				</div>

				<div className="relative z-10 mx-auto max-w-[1200px] px-4 md:px-6">
					<div className="flex flex-col gap-8 md:gap-16">
						{/* Title */}
						<h2 className="font-parkinsans text-[32px] leading-[42px] font-medium text-blue-800 md:text-[48px] md:leading-[64px]">
							{title}
						</h2>

						{/* Benefits Cards */}
						<div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-[30px]">
							{beneficios.map((beneficio) => (
								<div
									key={beneficio.id}
									className="flex flex-col gap-4 rounded-[20px] border border-neutral-200 bg-white p-6 shadow-sm md:gap-6 md:rounded-[24px] md:p-8"
								>
									{/* Icon */}
									<div className="flex h-10 w-10 items-center justify-center rounded-[20px] bg-blue-500 p-2 md:h-11 md:w-11 md:rounded-[24px] md:p-2.5">
										<Icon
											name={beneficio.icon as any}
											className="h-5 w-5 text-white md:h-6 md:w-6"
										/>
									</div>

									{/* Content */}
									<div className="flex flex-col gap-3 md:gap-4">
										<h3 className="font-parkinsans text-[20px] leading-[28px] font-medium text-blue-800 md:text-[24px] md:leading-[32px]">
											{beneficio.title}
										</h3>
										<p className="font-inter text-[14px] leading-[22px] font-normal text-neutral-600 not-italic md:text-[16px] md:leading-[26px]">
											{beneficio.description}
										</p>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</section>
		)
	},
)

SegurosBeneficiosSection.displayName = 'SegurosBeneficiosSection'

export { SegurosBeneficiosSection }
